import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, ButtonGroup, Divider, Heading, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { LeaseArtifactType, LeaseType } from '@zg-rentals/ts-leasing-client';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { Lease, Property } from '../../../models';
import { useLeasingUrlParams } from '../../../hooks';
import {
  useCreateNewLeaseMutation,
  useLeaseQuery,
  usePreviewUrlQuery,
  usePropertyInfoQuery,
  useWebAnalyticsForListingQuery,
} from '../../../utils/queries';
import { StyledZDocsEditor } from '../../../constants/styleConstants';
import DocumentPreviewProgressIndicator from '../../../components/DocumentPreviewProgressIndicator';
import FeedbackModal from './FeedbackModal';
import {
  emitCloseGeneratedLeaseEvent,
  emitEditAndShareGeneratedLeaseEvent,
  emitViewLeasePreviewEvent,
} from '../../../../analytics/events';

export function GenerateAndPreview() {
  const history = useHistory();
  const { propertyId, leaseId } = useLeasingUrlParams();
  const { enqueueToast } = useToast();
  const createLeaseMutation = useCreateNewLeaseMutation();
  const propertyQuery = usePropertyInfoQuery(propertyId, !!propertyId);
  const property = propertyQuery.data ? new Property(propertyQuery.data) : undefined;
  const hasStartedCreation = useRef(false);
  const [createdLeaseId, setCreatedLeaseId] = useState<number | null>(leaseId ? Number(leaseId) : null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [cachedPreviewUrl, setCachedPreviewUrl] = useState<string | null>(null);
  const errorToastShownRef = useRef(false);
  const initialValidationDoneRef = useRef(false);
  const { user } = useRentalManagerUser();

  const previewUrlQuery = usePreviewUrlQuery(createdLeaseId, LeaseArtifactType.LEASE, !!createdLeaseId, true);
  const leaseQuery = useLeaseQuery(createdLeaseId, { enabled: !!createdLeaseId });
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const lease = leaseQuery.data;
  const isLoaded = !previewUrlQuery.isLoading && !!previewUrlQuery.data?.url;

  const streetAddress = property?.streetAddress || 'your property';
  const isGenerating = !createdLeaseId;
  const previewUrl = previewUrlQuery.data?.url || cachedPreviewUrl;

  useEffect(() => {
    if (previewUrlQuery.data?.url) {
      setCachedPreviewUrl(previewUrlQuery.data.url);
    }
  }, [previewUrlQuery.data?.url]);

  useEffect(() => {
    if (!isGenerating || hasStartedCreation.current || !propertyId) {
      return;
    }

    hasStartedCreation.current = true;

    createLeaseMutation.mutate(
      {
        postData: {
          propertyId,
          leaseType: LeaseType.ZILLOW,
        },
      },
      {
        onSuccess: (response) => {
          if (response?.leaseId) {
            setCreatedLeaseId(response.leaseId);
            history.replace(Lease.getReviewPath(response.leaseId));
          } else {
            enqueueToast(<Toast tone="critical">We couldn’t load your lease</Toast>);
            history.replace(Property.getLeasesDashboardPath(property?.listingAlias));
          }
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">We couldn’t load your lease</Toast>);
          history.replace(Property.getLeasesDashboardPath(property?.listingAlias));
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating, propertyId]);

  useEffect(() => {
    if (isLoaded && !lease?.isArchived) {
      enqueueToast(
        <Toast tone="info">
          The lease may differ from the listing. Review it carefully before requesting a signature.
        </Toast>,
        { duration: 10000 },
      );
    }
  }, [isLoaded, lease?.isArchived, enqueueToast]);

  useEffect(() => {
    if (!lease || initialValidationDoneRef.current) return;

    initialValidationDoneRef.current = true;

    if (lease.isArchived) {
      history.push(Property.getLeasesDashboardPath(property?.listingAlias));
      enqueueToast(<Toast tone="warning">This lease has been deleted and can no longer be viewed</Toast>);
    } else if (!lease.isEditable()) {
      history.push(Property.getLeasesDashboardPath(property?.listingAlias));
      enqueueToast(<Toast tone="warning">This lease is no longer editable and can’t be modified from here</Toast>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lease]);

  useEffect(() => {
    if (previewUrlQuery.isError && !errorToastShownRef.current) {
      errorToastShownRef.current = true;
      enqueueToast(
        <Toast
          tone="critical"
          actionButton={
            <TextButton
              onImpact
              onClick={() => {
                errorToastShownRef.current = false;
                previewUrlQuery.refetch();
              }}
            >
              Retry
            </TextButton>
          }
        >
          We couldn’t load the lease preview
        </Toast>,
      );
    }
  }, [previewUrlQuery.isError, previewUrlQuery, enqueueToast]);

  useEffect(() => {
    if (isLoaded && webAnalyticsQuery.data) {
      emitViewLeasePreviewEvent({ user, listing: webAnalyticsQuery.data });
    }
  }, [isLoaded, user, webAnalyticsQuery.data]);

  function handleClose() {
    if (!previewUrl) {
      history.push(Property.getLeasesDashboardPath(property?.listingAlias));
    } else if (previewUrlQuery.isError) {
      window.location.href = Property.getPropertyDashboardOverviewPath(propertyId);
    } else {
      setShowFeedbackModal(true);
    }
  }

  function handleCloseClick() {
    emitCloseGeneratedLeaseEvent({ user, listing: webAnalyticsQuery.data });
    setShowFeedbackModal(true);
  }

  function handleFeedbackClose() {
    setShowFeedbackModal(false);
  }

  function handleEditAndShare() {
    emitEditAndShareGeneratedLeaseEvent({ user, listing: webAnalyticsQuery.data });
    history.push(Lease.getSummaryPath(createdLeaseId));
  }

  return (
    <>
      <Modal.Root
        size="fullScreen"
        open
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
        useDismissProps={{ escapeKey: false, outsidePress: false }}
      >
        <Modal.Portal>
          <Modal.Backdrop>
            <Modal.Content>
              <Modal.Header>
                <Heading level={1}>Lease sample for {streetAddress}</Heading>
              </Modal.Header>
              <Divider />
              <Modal.Body css={{ padding: '0 !important' }}>
                {!isLoaded && (
                  <DocumentPreviewProgressIndicator
                    progressIndicatorDuration={3000}
                    label="Hang tight"
                    subLabel="We’re loading your lease document"
                  />
                )}
                {previewUrlQuery.data?.url && <StyledZDocsEditor url={previewUrlQuery.data.url} />}
              </Modal.Body>
              <Divider />
              <Modal.Footer>
                <ButtonGroup aria-label="lease review actions">
                  <TextButton onClick={handleCloseClick} disabled={!isLoaded}>
                    Close
                  </TextButton>
                  <Button tone="brand" emphasis="filled" onClick={handleEditAndShare} disabled={!isLoaded}>
                    Edit and share
                  </Button>
                </ButtonGroup>
              </Modal.Footer>
              <Modal.CloseButton />
            </Modal.Content>
          </Modal.Backdrop>
        </Modal.Portal>
      </Modal.Root>
      <FeedbackModal isOpen={showFeedbackModal} onClose={handleFeedbackClose} />
    </>
  );
}

export default GenerateAndPreview;
